import { useCallback, useEffect, useRef } from 'react'
import { getBoundsAroundPoint, type MapBounds } from 'lib/maps/bounds'
import type { MapCoordinate } from 'tripPlanner/utils/mapMarkerTransforms'
import { DEFAULT_PADDING } from 'tripPlanner/components/Chat/chatMapConstants'
import { EXPLORE_STOP_FRAME_KM } from 'tripPlanner/utils/explorePlacesConstants'
import type { ChatExploreCategory } from 'tripPlanner/config/exploreCategories'

interface Args {
  category: ChatExploreCategory | undefined
  /** Where to land. Undefined while the viewport already fits and nothing needs to move. */
  target: MapCoordinate | undefined
  /** A drawer handed its own search over, and frames the results itself once they land. */
  isHandoffActive?: boolean
  paneRef: React.RefObject<HTMLElement | null>
  fitToCoordinates: (coordinates: Array<MapCoordinate>) => void
}

/**
 * Shaped to the pane, not square: a square box fitted into a wide pane binds on height and leaves
 * more ground across than `maxSpanKm`, which puts the framed area past the search gate.
 */
export function getFrameBounds(
  point: MapCoordinate,
  maxSpanKm: number,
  pane: { width: number; height: number },
): MapBounds {
  const fittableWidth = Math.max(pane.width - DEFAULT_PADDING.left - DEFAULT_PADDING.right, 1)
  const fittableHeight = Math.max(pane.height - DEFAULT_PADDING.top - DEFAULT_PADDING.bottom, 1)
  const isLandscape = pane.width >= pane.height

  const longSideKm = maxSpanKm * (isLandscape ?
    fittableWidth / pane.width :
    fittableHeight / pane.height)
  const shortSideKm = longSideKm * (isLandscape ?
    fittableHeight / fittableWidth :
    fittableWidth / fittableHeight)

  return isLandscape ?
      getBoundsAroundPoint(point, longSideKm, shortSideKm) :
      getBoundsAroundPoint(point, shortSideKm, longSideKm)
}

/** Frames once per selection, so panning afterwards is the traveller's own. */
export function useExploreCamera({
  category,
  target,
  isHandoffActive,
  paneRef,
  fitToCoordinates,
}: Args): (point: MapCoordinate) => void {
  const decidedForRef = useRef<ChatExploreCategory | undefined>(undefined)

  const frameCoordinates = useCallback((point: MapCoordinate) => {
    const pane = paneRef.current
    if (!pane) return

    const frame = getFrameBounds(point, EXPLORE_STOP_FRAME_KM, {
      width: pane.clientWidth,
      height: pane.clientHeight,
    })
    fitToCoordinates([
      { lat: frame.north, lng: frame.west },
      { lat: frame.south, lng: frame.east },
    ])
  }, [fitToCoordinates, paneRef])

  useEffect(() => {
    if (!category) {
      decidedForRef.current = undefined
      return
    }
    if (decidedForRef.current === category) return

    // Marked decided even with nothing to frame: a chip picked at street level applies in place,
    // and zooming out afterwards should offer Search this area rather than snap back.
    decidedForRef.current = category
    /*
     * A handoff already framed the stop, and `useExploreHandoffCamera` frames its results once they
     * land. Framing `target` here would be a second fit issued after that one, and only the last
     * fitBoundsKey is honoured -- for LE offers, which are fetched from wherever the camera settles,
     * that means searching the viewport centre's region instead of the stop's.
     */
    if (isHandoffActive) return
    if (target) frameCoordinates(target)
  }, [category, frameCoordinates, isHandoffActive, target])

  return frameCoordinates
}
